import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Container,
  TablePagination
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ApprovedOffers = () => {
  const [offers, setOffers] = useState({
    approved: [],
    rejected: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  
  // Pagination states
  const [approvedPage, setApprovedPage] = useState(0);
  const [rejectedPage, setRejectedPage] = useState(0);
  const [rowsPerPage] = useState(5);
  
  const navigate = useNavigate();
  const [currencySymbol, setCurrencySymbol] = useState('â‚¹');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.company_id) {
          const res = await fetch(`${API_BASE_URL}/api/cmp/${payload.company_id}`, { headers: { Authorization: `Bearer ${token}` } });
          const data = await res.json();
          if (data && data.data && data.data.currency_symbol) {
            setCurrencySymbol(data.data.currency_symbol);
          }
        }
      } catch (err) {
        // console.error("Error fetching company config", err);
      }
    };
    fetchConfig();
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      setLoading(true);
      // Fetch approved offers
      const approvedResponse = await fetch(`${API_BASE_URL}/api/offers/approved`, {
        headers: {
          Authorization: `Bearer ${token}`,
        
        }
      });
      if (!approvedResponse.ok) {
        throw new Error(`HTTP error! Status: ${approvedResponse.status}`);
      }
      const approvedData = await approvedResponse.json();
      
      // Fetch rejected offers (assuming endpoint exists)
      const rejectedResponse = await fetch(`${API_BASE_URL}/api/offers/rejected`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      if (!rejectedResponse.ok) {
        throw new Error(`HTTP error! Status: ${rejectedResponse.status}`);
      }
      const rejectedData = await rejectedResponse.json();
      
      setOffers({
        approved: approvedData,
        rejected: rejectedData
      });
    } catch (error) {
      //console.error("Error fetching offers:", error);
      setAlert({
        open: true,
        message: "Failed to fetch offers: " + error.message,
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendOffer = (offer) => {
    setSelectedOffer(offer);
    setDialogOpen(true);
  };

  const handleViewDetails = (offer) => {
    setSelectedOffer(offer);
    setDetailsDialogOpen(true);
  };
  
  const handleEditOffer = (offer) => {
    // Navigate to the offer form with the offer ID
    navigate(`/offer`, { state: { offer } });
  };

  const handleDownloadOfferLetter = async (offerLetterPath) => {
    try {
       const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      const response = await fetch(`${API_BASE_URL}/api/offers/download/${offerLetterPath}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = offerLetterPath.split('/').pop() || 'offer-letter.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      //console.error("Error downloading offer letter:", error);
      setAlert({
        open: true,
        message: "Failed to download offer letter: " + error.message,
        severity: "error"
      });
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedOffer(null);
  };
  
  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
    setSelectedOffer(null);
  };

  const handleSendConfirm = async () => {
    if (!selectedOffer) return;
    
    try {
       const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      setSendingOffer(true);
      const response = await fetch(`${API_BASE_URL}/api/offers/${selectedOffer.offerId}/send`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }
      
      const data = await response.json();
      
      setAlert({
        open: true,
        message: "Offer sent to candidate successfully!",
        severity: "success"
      });
      
      handleDialogClose();
      fetchOffers(); 
      
    } catch (error) {
      //console.error("Error sending offer:", error);
      setAlert({
        open: true,
        message: `Failed to send offer: ${error.message}`,
        severity: "error"
      });
    } finally {
      setSendingOffer(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Pagination handlers
  const handleApprovedPageChange = (event, newPage) => {
    setApprovedPage(newPage);
  };

  const handleRejectedPageChange = (event, newPage) => {
    setRejectedPage(newPage);
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return `${currencySymbol}${Number(amount).toLocaleString(undefined)}`;
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const renderOffersTable = (offerList, isRejected = false) => {
    if (offerList.length === 0) {
      return (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No {isRejected ? "rejected" : "approved"} offers to display
            </Typography>
          </CardContent>
        </Card>
      );
    }

    // Get current page and calculate pagination
    const currentPage = isRejected ? rejectedPage : approvedPage;
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedOffers = offerList.slice(startIndex, endIndex);
    
    return (
      <Paper sx={{ mt: 3 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Candidate</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Salary Fixed</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Joining Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Offer Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOffers.map((offer) => (
                <TableRow key={offer.offerId} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {offer.candidateName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {offer.candidateEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{offer.jobTitle}</TableCell>
                  <TableCell>{formatCurrency(offer.salaryFixed)}</TableCell>
                  <TableCell>{formatDate(offer.joiningDate)}</TableCell>
                  <TableCell>{formatDate(offer.offerDate)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={isRejected ? "Changes Required" : (offer.emailSent ? "Sent" : "Approved")} 
                      size="small"
                      color={isRejected ? "warning" : (offer.emailSent ? "success" : "primary")}
                      icon={offer.emailSent ? <CheckCircleOutlineIcon fontSize="small" /> : null}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          color="info"
                          onClick={() => handleViewDetails(offer)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {isRejected && (
                        <Tooltip title="Edit & Resubmit">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleEditOffer(offer)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {!isRejected && offer.status === 'Approved' && !offer.emailSent && (
                        <Tooltip title="Send to Candidate">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleSendOffer(offer)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination Component */}
        <TablePagination
          component="div"
          count={offerList.length}
          page={currentPage}
          onPageChange={isRejected ? handleRejectedPageChange : handleApprovedPageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5]}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
          sx={{
            borderTop: '1px solid #e0e0e0',
            '.MuiTablePagination-toolbar': {
              paddingLeft: 2,
              paddingRight: 2,
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              margin: 0,
            }
          }}
        />
      </Paper>
    );
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
    <Paper elevation={3} sx={{ p: 4,  borderRadius: 2, // Rounded corners
  border: '2px solid',
  borderColor: 'grey.300', // Subtle professional border
  boxShadow: 4,  }}>
      <Typography variant="h4" gutterBottom  fontWeight="bold">
        Offer Management
      </Typography>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2, fontSize: '0.95rem' }}>
        <strong>Important:</strong> Only offers that have been officially approved by the <strong>Hiring Manager</strong> will appear in the "Approved Offers" queue. Once an offer appears here, you may securely send the official offer letter to the candidate.
      </Alert>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}  variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile>
          <Tab label="Approved Offers" />
          <Tab label="Rejected Offers" />
        </Tabs>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && renderOffersTable(offers.approved)}
          {activeTab === 1 && renderOffersTable(offers.rejected, true)}
        </>
      )}
      
      {/* Send Offer Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        
      >
        <DialogTitle sx={{ 
          
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
           
            <Typography variant="h5" fontWeight="600">
              Send Job Offer
            </Typography>
          </Box>
          <Button
            onClick={handleDialogClose}
            sx={{ 
              color: 'white',
              minWidth: 'auto',
              p: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
           
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedOffer && (
            <Box>
              {/* Candidate Information Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  m: 3, 
                  p: 3, 
                  bgcolor: '#f8fafc', 
                  border: '1px solid #e2e8f0',
                  borderRadius: 2
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  
                  <Typography variant="h6" fontWeight="600" color="text.primary">
                    Candidate Information
                  </Typography>
                </Box>
                
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
                  <Box display="flex" alignItems="center" gap={2}>
                   
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Full Name
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {selectedOffer.candidateName}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={2}>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email Address
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {selectedOffer.candidateEmail}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Offer Details Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  mx: 3, 
                  mb: 3, 
                  p: 3, 
                  
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  
                  <Typography variant="h6" fontWeight="600" color="text.primary">
                    Offer Details
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={3}>
                  {/* Position */}
                  <Box display="flex" alignItems="center" gap={2}>
                   
                    <Box flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        Position
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {selectedOffer.jobTitle}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={2}>
                   
                   <Box flex={1}>
                     <Typography variant="caption" color="text.secondary">
                       Salary Fixed
                     </Typography>
                     <Typography variant="body1" fontWeight="500">
                       {selectedOffer.salaryFixed}
                     </Typography>
                   </Box>
                 
                   
                   <Box flex={1}>
                     <Typography variant="caption" color="text.secondary">
                      Variable Salary
                     </Typography>
                     <Typography variant="body1" fontWeight="500">
                       {selectedOffer.salaryVariable}
                     </Typography>
                   </Box>
                
 </Box>
                

                  {/* Joining Bonus */}
                  {selectedOffer.joiningBonus && (
                    <Box display="flex" alignItems="center" gap={2}>
                     
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          Joining Bonus
                        </Typography>
                        <Typography variant="body1" fontWeight="500" color="warning.main">
                          {formatCurrency(selectedOffer.joiningBonus)}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Joining Date */}
                  <Box display="flex" alignItems="center" gap={2}>
                    
                    <Box flex={1}>
                      <Typography variant="caption" color="text.secondary">
                        Expected Joining Date
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDate(selectedOffer.joiningDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

             
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleDialogClose} 
            color="inherit"
            size="large"
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendConfirm}
            color="primary"
            variant="contained"
            disabled={sendingOffer}
            startIcon={sendingOffer ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            size="large"
            sx={{ 
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            {sendingOffer ? 'Sending Offer...' : 'Send Offer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Offer Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={handleDetailsDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Offer Details
        </DialogTitle>
        <DialogContent>
          {selectedOffer && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Candidate Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedOffer.candidateName}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedOffer.candidateEmail}
                </Typography>
                <Typography variant="body2">
                  <strong>Position:</strong> {selectedOffer.jobTitle}
                </Typography>
                <Typography variant="body2">
                  <strong>Joining Date:</strong> {formatDate(selectedOffer.joiningDate)}
                </Typography>
              </Box>
              <Divider/>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Typography variant="body2">
                  <strong>Fixed Salary:</strong> {formatCurrency(selectedOffer.salaryFixed)}
                </Typography>
                <Typography variant="body2">
                  <strong>Variable Pay:</strong> {selectedOffer.salaryVariable ? formatCurrency(selectedOffer.salaryVariable) : "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Joining Bonus:</strong> {selectedOffer.joiningBonus ? formatCurrency(selectedOffer.joiningBonus) : "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>ESOP:</strong> {selectedOffer.esop || "-"}
                </Typography>
              </Box>
              
              {selectedOffer.approvalComments && (
                <>
                  <Typography variant="h6" gutterBottom  >
                    Comments
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f9f9f9' }}>
                    <Typography variant="body2">
                      {selectedOffer.approvalComments}
                    </Typography>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailsDialogClose} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alert.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
  
    </Paper>
    </Container>
  );
};

export default ApprovedOffers;